import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Mail, CheckCircle2, AlertCircle } from 'lucide-react';
import { supabase } from '../lib/supabase';
import AuthLayout from './AuthLayout';

// /auth/reset cumple dos roles:
//   1) Si NO hay sesión, pedimos email para enviar el correo de recuperación.
//   2) Si llegamos desde el link del email (Supabase trae access_token en hash),
//      auto-detectado por detectSessionInUrl=true, se permite definir nueva contraseña.

export default function ResetPasswordScreen() {
  const navigate = useNavigate();
  const [mode, setMode] = useState('request'); // 'request' | 'update'
  const [email, setEmail] = useState('');
  const [pwd, setPwd] = useState('');
  const [pwd2, setPwd2] = useState('');
  const [info, setInfo] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // Supabase emite 'PASSWORD_RECOVERY' cuando el usuario llega vía link de recovery.
    const { data: sub } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'PASSWORD_RECOVERY') setMode('update');
    });
    return () => sub.subscription?.unsubscribe();
  }, []);

  const submitRequest = async (e) => {
    e.preventDefault();
    setError(null); setInfo(null); setLoading(true);
    const { error: err } = await supabase.auth.resetPasswordForEmail(email.trim(), {
      redirectTo: `${window.location.origin}/auth/reset`,
    });
    setLoading(false);
    if (err) { setError(err.message); return; }
    setInfo('Si el correo existe en el sistema, recibirás un link de recuperación. Revisa tu bandeja institucional y la carpeta de spam.');
  };

  const submitUpdate = async (e) => {
    e.preventDefault();
    setError(null);
    if (pwd.length < 10) { setError('La contraseña debe tener al menos 10 caracteres.'); return; }
    if (pwd !== pwd2) { setError('Las contraseñas no coinciden.'); return; }
    setLoading(true);
    const { error: err } = await supabase.auth.updateUser({ password: pwd });
    setLoading(false);
    if (err) { setError(err.message); return; }
    setInfo('Tu contraseña ha sido actualizada. Ya puedes iniciar sesión.');
    setTimeout(() => navigate('/login', { replace: true }), 1800);
  };

  if (mode === 'update') {
    return (
      <AuthLayout
        title="Define una nueva contraseña"
        subtitle="Llegaste desde el link de recuperación. Define una nueva contraseña."
      >
        <form onSubmit={submitUpdate} className="space-y-4" data-testid="reset-update-form">
          <input type="password" required autoComplete="new-password" placeholder="Nueva contraseña" value={pwd}
                 onChange={(e) => setPwd(e.target.value)}
                 className="w-full px-3 py-3 rounded-xl border border-[#DDE6F7] bg-[#FAFBFF] text-[#0B1B5E] focus:border-[#5346E6] focus:bg-white focus:outline-none text-sm"
                 data-testid="reset-input-new-password" />
          <input type="password" required autoComplete="new-password" placeholder="Confirmar contraseña" value={pwd2}
                 onChange={(e) => setPwd2(e.target.value)}
                 className="w-full px-3 py-3 rounded-xl border border-[#DDE6F7] bg-[#FAFBFF] text-[#0B1B5E] focus:border-[#5346E6] focus:bg-white focus:outline-none text-sm"
                 data-testid="reset-input-confirm-password" />
          {error && <div className="flex gap-2 p-3 rounded-xl bg-[#FFE9E9] border border-[#FFC4C4] text-[#9B1C1C] text-xs" role="alert" data-testid="reset-error">
            <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" /><span>{error}</span></div>}
          {info && <div className="flex gap-2 p-3 rounded-xl bg-[#E1F6EA] border border-[#A6E1BD] text-[#1E5A38] text-xs" role="status" data-testid="reset-info">
            <CheckCircle2 className="w-4 h-4 shrink-0 mt-0.5" /><span>{info}</span></div>}
          <button type="submit" disabled={loading}
                  className="w-full py-3 rounded-xl bg-[#0B1B5E] hover:bg-[#5346E6] text-white font-bold text-sm shadow-[0_10px_24px_-10px_rgba(11,27,94,0.55)] disabled:bg-[#8089AE]"
                  data-testid="reset-update-submit">
            {loading ? 'Guardando…' : 'Guardar nueva contraseña'}
          </button>
        </form>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title="Recuperar contraseña"
      subtitle="Te enviaremos un link a tu correo institucional para que definas una nueva contraseña."
      footer={<Link to="/login" className="text-[#5346E6] font-semibold hover:underline" data-testid="reset-link-back">Volver al login</Link>}
    >
      <form onSubmit={submitRequest} className="space-y-4" data-testid="reset-request-form">
        <label className="block text-sm font-semibold text-[#0B1B5E]">
          Correo institucional
          <div className="relative mt-1">
            <Mail className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#7B86A6]" aria-hidden />
            <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                   placeholder="tu.nombre@colegio.cl"
                   className="w-full pl-9 pr-3 py-3 rounded-xl border border-[#DDE6F7] bg-[#FAFBFF] text-[#0B1B5E] focus:border-[#5346E6] focus:bg-white focus:outline-none text-sm"
                   data-testid="reset-input-email" />
          </div>
        </label>
        {error && <div className="flex gap-2 p-3 rounded-xl bg-[#FFE9E9] border border-[#FFC4C4] text-[#9B1C1C] text-xs" role="alert" data-testid="reset-error">
          <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" /><span>{error}</span></div>}
        {info && <div className="flex gap-2 p-3 rounded-xl bg-[#E1F6EA] border border-[#A6E1BD] text-[#1E5A38] text-xs" role="status" data-testid="reset-info">
          <CheckCircle2 className="w-4 h-4 shrink-0 mt-0.5" /><span>{info}</span></div>}
        <button type="submit" disabled={loading}
                className="w-full py-3 rounded-xl bg-[#0B1B5E] hover:bg-[#5346E6] text-white font-bold text-sm shadow-[0_10px_24px_-10px_rgba(11,27,94,0.55)] disabled:bg-[#8089AE]"
                data-testid="reset-request-submit">
          {loading ? 'Enviando…' : 'Enviar link de recuperación'}
        </button>
      </form>
    </AuthLayout>
  );
}
